
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { VirtualTeamMember, Account } from "../types";

export const useVirtualTeam = (selectedAccount: Account | null) => {
  const [virtualTeam, setVirtualTeam] = useState<VirtualTeamMember[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (selectedAccount?.company_id) {
      fetchVirtualTeam();
    }
  }, [selectedAccount]);

  const fetchVirtualTeam = async () => {
    if (!selectedAccount?.company_id) return;
    setIsLoading(true);
    try {
      const { data, error } = await supabase
        .from('virtual_account_team')
        .select('*')
        .eq('company_id', selectedAccount.company_id);
      
      if (error) throw error;
      setVirtualTeam(data || []);
    } catch (error) {
      console.error('Error fetching virtual team:', error);
      toast.error('Failed to load virtual team');
    } finally {
      setIsLoading(false);
    }
  };

  const addTeamMember = async (memberData: Omit<VirtualTeamMember, 'id'>) => {
    if (!selectedAccount?.company_id) {
      toast.error('No account selected');
      return false;
    }

    try {
      // Get current user ID
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        toast.error('User not authenticated');
        return false;
      }

      const { error } = await supabase
        .from('virtual_account_team')
        .insert([{
          company_id: selectedAccount.company_id,
          user_id: user.id,
          ...memberData
        }]);
      
      if (error) throw error;
      toast.success('Team member added successfully');
      fetchVirtualTeam();
      return true;
    } catch (error) {
      console.error('Error adding team member:', error);
      toast.error('Failed to add team member');
      return false;
    }
  };

  const updateTeamMember = async (id: string, memberData: Omit<VirtualTeamMember, 'id'>) => {
    try {
      // Get current user ID for ownership consistency
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        toast.error('User not authenticated');
        return false;
      }

      const { error } = await supabase
        .from('virtual_account_team')
        .update({
          user_id: user.id,
          ...memberData
        })
        .eq('id', id);
      
      if (error) throw error;
      toast.success('Team member updated successfully');
      fetchVirtualTeam();
      return true;
    } catch (error) {
      console.error('Error updating team member:', error);
      toast.error('Failed to update team member');
      return false;
    }
  };

  const deleteTeamMember = async (id: string) => {
    try {
      const { error } = await supabase
        .from('virtual_account_team')
        .delete()
        .eq('id', id);
      
      if (error) throw error;
      toast.success('Team member deleted successfully');
      fetchVirtualTeam();
      return true;
    } catch (error) {
      console.error('Error deleting team member:', error);
      toast.error('Failed to delete team member');
      return false;
    }
  };

  return {
    virtualTeam,
    isLoading,
    addTeamMember,
    updateTeamMember,
    deleteTeamMember
  };
};
